<template>
  <div class="medical-record">
    <!-- 图片预览组件 -->
    <div v-if="showImagePreview" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-container" @mousedown.stop="startDrag" @mousemove.stop="onDrag" @mouseup.stop="stopDrag" @mouseleave="stopDrag" @wheel.stop="onZoom">
        <img 
          :src="previewImageUrl" 
          alt="预览图片" 
          class="preview-image" 
          :style="{ transform: `translate(${translateX}px, ${translateY}px) scale(${scale})` }"
        />
        <div class="close-preview" @click.stop="closeImagePreview">×</div>
        <div class="zoom-controls">
          <button @click.stop="zoomIn">+</button>
          <button @click.stop="zoomOut">-</button>
          <button @click.stop="resetZoom">重置</button>
        </div>
      </div>
    </div>
    <div class="patient-info">
      <div class="avatar">{{ patientName.charAt(0) }}</div>
      <div class="info">
        <div class="name">{{ patientName }}</div>
        <div class="date">{{ currentDate }}</div>
      </div>
    </div>

    <div class="tabs">
      <div 
        v-for="(tab, index) in tabs" 
        :key="index"
        :class="['tab-item', { active: activeTab === index }]"
        @click="activeTab = index"
      >
        {{ tab }}
      </div>
    </div>

    <div class="content" v-if="activeTab === 0">
      <div class="section">
        <div class="section-title">一、照片</div>
        <div class="photo-category-title">舌苔图</div>
        <div class="photos photo-grid" v-if="photoList && photoList.length > 0">
          <div class="photo-item" v-for="(item, index) in photoList" :key="index">
            <img :src="addThumbnailParams(item.path || item)" :alt="'舌苔照片'" class="photo-image" @error="handleImageError($event)" @click="openImagePreview(item.path || item)" />
          </div>
        </div>

        <div class="photo-category-title">其他图</div>
        <div class="photos photo-grid" v-if="otherPhotoList && otherPhotoList.length > 0">
          <div class="photo-item" v-for="(item, index) in otherPhotoList" :key="index">
            <img :src="addThumbnailParams(item.path || item)" :alt="'其他照片'" class="photo-image" @error="handleImageError($event)" @click="openImagePreview(item.path || item)" />
          </div>
        </div>

        <div class="empty-state" v-if="(!photoList || photoList.length === 0) && (!otherPhotoList || otherPhotoList.length === 0)">
          <i class="empty-icon">📷</i>
          <div class="empty-text">暂无照片数据</div>
        </div>
      </div>

      <div class="section">
        <div class="section-title">二、病史</div>
        <div class="history-list" v-if="medicalHistoryList && medicalHistoryList.length > 0">
          <div class="history-item" v-for="(item, index) in medicalHistoryList" :key="index">
            <div class="history-date">{{ item.date }}</div>
            <div class="history-content" style="white-space: pre-line;">{{ item.content }}</div>
          </div>
        </div>
        <div class="empty-state" v-else>暂无病史数据</div>
      </div>
      
      <div class="section">
        <div class="section-title">三、异常指标</div>
        <div class="lab-reports" v-if="anomalyIndexList && anomalyIndexList.length > 0">
          <div class="lab-report" v-for="(report, index) in anomalyIndexList" :key="index">
            <div class="report-date" @click="toggleReportDetails(index)">
              {{ report.date }}
              <span class="toggle-icon">{{ expandedReports[index] ? '▼' : '▶' }}</span>
            </div>
            <div class="report-details" v-if="expandedReports[index]">
              <div class="report-item" v-for="(detail, detailIndex) in report.details" :key="detailIndex">
                <span class="item-name">{{ detail.name }}:</span>
                <span :class="['item-value', detail.range]">
                  {{ detail.value }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="empty-state" v-else>暂无异常指标数据</div>
        
        <div class="section-title">指标趋势图</div>
        <div class="charts" v-if="anomalyIndexLineList && anomalyIndexLineList.length > 0">
          <div class="chart" v-for="(indicator, index) in anomalyIndexLineList" :key="index">
            <div class="chart-title">{{ indicator.name }}趋势图 <span class="unit">{{ getUnitByIndicator(indicator.name) }}</span></div>
            <div class="chart-container">
              <div class="chart-y-axis">
                <div v-for="(value, index) in getYAxisValues(indicator)" :key="index">{{ value }}</div>
              </div>
              <div class="chart-line">
                <svg width="100%" height="100%" viewBox="0 0 300 100" preserveAspectRatio="none">
                  <polyline
                    :points="getChartPoints(indicator.details)"
                    fill="none"
                    stroke="#4285f4"
                    stroke-width="2"
                  />
                </svg>
              </div>
            </div>
            <div class="chart-x-axis">
              <div v-for="(label, labelIndex) in getXAxisLabels(indicator)" :key="labelIndex">{{ label }}</div>
            </div>
          </div>
        </div>
        <div class="empty-state" v-else>暂无指标趋势图数据</div>
      </div>
    </div>

    <div class="content" v-if="activeTab === 1">
      <div class="section">
        <div class="section-title">化验报告</div>
        <!-- <div class="lab-report-search">
          <input type="text" v-model="labReportSearch" placeholder="搜索图像描述/图中文字" class="lab-report-input" />
        </div> -->
        <div v-if="analysisReportList && analysisReportList.length > 0">
          <div v-for="(group, groupIndex) in analysisReportList" :key="groupIndex" class="lab-report-group">
            <div class="lab-report-group-title">{{ group.date }}</div>
            <div class="lab-report-grid">
              <div v-for="(img, imgIndex) in group.imageUrl" :key="imgIndex" class="lab-report-img-item">
                <img :src="addThumbnailParams(img)" :alt="img" class="lab-report-img" @click="openImagePreview(img)" />
              </div>
            </div>
          </div>
        </div>
        <div class="empty-state" v-else>暂无化验报告数据</div>
      </div>
    </div>

    <div class="content" v-if="activeTab === 2">
      <div class="section">
        <div class="section-title">文档</div>
        <div class="document-list" v-if="documentList && documentList.length > 0">
          <div class="document-item" v-for="(doc, index) in documentList" :key="index" @click="downloadDocument(doc)">
            <div class="document-info">
              <div class="document-name">{{ doc.fileName }}</div>
              <div class="document-type">{{ doc.type }} {{ doc.size }}</div>
            </div>
            <div class="document-date">{{ doc.date }}</div>
            <div class="document-icon" :class="doc.iconClass">
              <span>{{ doc.iconText }}</span>
            </div>
          </div>
        </div>
        <div class="empty-state" v-else>暂无文档数据</div>
      </div>
    </div>

    <div class="content" v-if="activeTab === 3">
      <div class="section">
        <div class="empty-state">待开发</div>
        <!-- <div class="section-title">聊天记录</div> -->
        <!-- <div class="chat-records" v-if="chatMessages && chatMessages.length > 0">
          <div class="chat-date" v-for="(item, index) in chatMessages" :key="index">
            <div class="chat-date-divider">{{ item.date }}</div>
            <div class="chat-message" v-for="(message, childIndex) in item.details" :key="childIndex">
              <div class="chat-avatar">
                <div class="avatar-circle">{{ message.sender ? message.sender.charAt(0) : '用户' }}</div>
              </div>
              <div class="chat-bubble">
                <div v-if="message.type === 'ChatRecordImage'" class="chat-image">
                  <img :src="message.content" :alt="'聊天图片'" class="chat-img" @click="openImagePreview(message.content)" />
                </div>
                <div v-else class="chat-text">{{ message.content }}</div>
              </div>
            </div>
          </div>
        </div> -->
        <!-- <div class="empty-state" v-else>暂无聊天记录数据</div> -->
      </div>
    </div>

    <!-- <div class="footer">
      <div class="footer-btn">{{ patientName }}-病例总结报告</div>
    </div> -->
  </div>
</template>

<script>
import axios from 'axios';

var domain = "https://catalog.lianty.cn/mdt"

// var doamin = "http://laboratory.lianty.cn"

export default {
  name: 'MedicalRecord',
  data() {
    return {
      patientName: '',
      currentDate: '',
      activeTab: 0,
      tabs: ['病史描述', '检查报告', '相关文档', 'AI建议'],
      // 用户名参数，用于API调用
      username: '',
      // 照片数据
      photoList: [],
      // 其他照片
      otherPhotoList: [],
      // 历史记录数据
      medicalHistoryList: [],
      anomalyIndexList: [],
      expandedReports: {},
      anomalyIndexLineList: [],
      // 化验报告数据
      analysisReportList: [],
      // 搜索关键词
      labReportSearch: '',
      // 图片预览相关
      showImagePreview: false,
      previewImageUrl: '',
      // 图片拖动和缩放相关
      isDragging: false,
      startX: 0,
      startY: 0,
      translateX: 0,
      translateY: 0,
      scale: 1,
      initialScale: 1,
      // 缓存处理过的图片URL
      processedImageUrls: {},
      // 当前正在处理的URL队列
      processingQueue: [],
      // 是否正在处理队列
      isProcessingQueue: false
    };
  },
  created() {
    // 从URL中获取username参数
    const urlParams = new URLSearchParams(window.location.search);
    this.username = urlParams.get('username');

    // 根据username动态设置网页标题
    if (this.username) {
      document.title = `${this.username}的病案报告`;
    } else {
      document.title = '病案报告';
    }

    // 初始化API调用
    this.loadMedicalDataFromApi();
    this.loadMedicalReportFromApi();
    this.loadMedicalDocFromApi();
  },

  methods: {
    // 添加缩略图参数 - 支持串行处理
    addThumbnailParams(url) {
      if (!url) {
        return '';
      }
      
      // 将请求添加到队列并返回处理结果
      return this.processImageUrl(url, 'thumb');
    },
    
    // 添加预览图参数 - 支持串行处理
    addPreviewParams(url) {
      if (!url) {
        return '';
      }
      
      // 将请求添加到队列并返回处理结果
      return this.processImageUrl(url, 'preview');
    },
    
    // 串行处理图片URL
    processImageUrl(url, type) {
      const cacheKey = url + '_' + type;
      
      // 检查缓存中是否已有处理过的URL
      if (this.processedImageUrls[cacheKey]) {
        return this.processedImageUrls[cacheKey];
      }
      
      // 根据类型确定参数
      const params = type === 'thumb' ? 'w=200&h=200' : 'w=1200&h=1200';
      
      // 检查URL是否已经包含参数
      const separator = url.includes('?') ? '&' : '?';
      const processedUrl = `${url}${separator}${params}`;
      
      // 存入缓存
      this.processedImageUrls[cacheKey] = processedUrl;
      
      // 添加到处理队列
      this.addToProcessingQueue(processedUrl);
      
      return processedUrl;
    },
    
    // 添加到处理队列并串行处理
    addToProcessingQueue(url) {
      this.processingQueue.push(url);
      
      // 如果当前没有正在处理的队列，开始处理
      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    },
    
    // 处理队列
    processQueue() {
      if (this.processingQueue.length === 0) {
        this.isProcessingQueue = false;
        return;
      }
      
      this.isProcessingQueue = true;
      const url = this.processingQueue.shift();
      
      // 预加载图片，确保串行加载
      const img = new Image();
      img.onload = () => {
        // 处理下一个URL
        setTimeout(() => {
          this.processQueue();
        }, 100); // 添加小延迟确保串行执行
      };
      img.onerror = () => {
        // 即使加载失败也继续处理下一个
        setTimeout(() => {
          this.processQueue();
        }, 100);
      };
      img.src = url;
    },
    
    // 处理图片加载错误
    handleImageError(event) {
      // 设置默认图片或添加错误样式
      event.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect width="80" height="80" fill="%23f0f0f0"/><text x="50%" y="50%" font-family="Arial" font-size="12" fill="%23999" text-anchor="middle" dominant-baseline="middle">暂无图片</text></svg>';
      event.target.classList.add('image-error');
    },
    
    // 打开图片预览
    openImagePreview(imageUrl) {
      console.log('打开图片预览:', imageUrl);
      // 确保图片URL有效
      if (!imageUrl) {
        console.error('图片URL无效');
        return;
      }
      
      // 使用添加了预览参数的URL
      this.previewImageUrl = this.addPreviewParams(imageUrl);
      this.showImagePreview = true;
      document.body.style.overflow = 'hidden'; // 防止背景滚动
      // 重置拖动和缩放状态
      this.resetZoom();
      
      // 预加载图片
      const img = new Image();
      img.src = this.previewImageUrl;
      
      // 强制DOM更新
      this.$nextTick(() => {
        console.log('图片预览组件已更新:', this.showImagePreview);
      });
    },
    
    // 关闭图片预览
    closeImagePreview() {
      this.showImagePreview = false;
      document.body.style.overflow = ''; // 恢复滚动
    },
    
    // 开始拖动
    startDrag(event) {
      this.isDragging = true;
      this.startX = event.clientX - this.translateX;
      this.startY = event.clientY - this.translateY;
    },
    
    // 拖动中
    onDrag(event) {
      if (this.isDragging) {
        this.translateX = event.clientX - this.startX;
        this.translateY = event.clientY - this.startY;
      }
    },
    
    // 停止拖动
    stopDrag() {
      this.isDragging = false;
    },
    
    // 缩放处理
    onZoom(event) {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      this.scale = Math.max(0.5, Math.min(3, this.scale + delta));
    },
    
    // 放大
    zoomIn() {
      this.scale = Math.min(3, this.scale + 0.2);
    },
    
    // 缩小
    zoomOut() {
      this.scale = Math.max(0.5, this.scale - 0.2);
    },
    
    // 重置缩放
    resetZoom() {
      this.translateX = 0;
      this.translateY = 0;
      this.scale = 1;
      this.isDragging = false;
    },
    
    // 从API加载医疗历史数据
    loadMedicalDataFromApi() {
      // 设置请求URL和参数
      const apiUrl = domain + '/medical/show/history';
      const params = { username: this.username };
      
      // 发起GET请求
      axios.get(apiUrl, { params })
        .then(response => {
          // 请求成功，处理返回的数据
          const apiData = response.data;
          console.log('API返回的数据:', apiData);
          // 更新组件数据
          if (apiData) {
            this.patientName = apiData.patientName; // 更新病人姓名
            this.currentDate = apiData.currentDate; // 更新当前日期
            this.photoList = apiData.photoList || []; // 更新照片列表
            this.otherPhotoList = apiData.otherList || []; // 更新其他照片列表
            this.medicalHistoryList = apiData.medicalHistoryList || [];// 历史病历
            this.anomalyIndexList = apiData.anomalyIndexList || []; // 异常指标
            this.anomalyIndexLineList = apiData.anomalyIndexLineList || [];
          }
        })
        .catch(error => {
          // 请求失败，使用本地数据作为备份
          console.error('API请求失败:', error);
        //   this.loadMedicalData();
        });
    },

    // 从API加载文档信息
    loadMedicalDocFromApi() {
      // 设置请求URL和参数
      const apiUrl = domain + '/medical/show/doc';
      const params = { username: this.username };
      
      // 发起GET请求
      axios.get(apiUrl, { params })
        .then(response => {
          // 请求成功，处理返回的数据
          const apiData = response.data;
          console.log('API返回的数据:', apiData);
          // 更新组件数据
          if (apiData) {
            this.documentList = apiData || []; // 更新照片列表
          }
        })
        .catch(error => {
          // 请求失败，使用本地数据作为备份
          console.error('API请求失败:', error);
        //   this.loadMedicalData();
        });
    },

    // // 从API加载聊天记录
    // loadMedicalChatFromApi() {
    //   // 设置请求URL和参数
    //   const apiUrl = 'http://laboratory.lianty.cn/medical/show/chat';
    //   const params = { username: this.username };
      
    //   // 发起GET请求
    //   axios.get(apiUrl, { params })
    //     .then(response => {
    //       // 请求成功，处理返回的数据
    //       const apiData = response.data;
    //       console.log('API返回的数据:', apiData);
    //       // 更新组件数据
    //       if (apiData) {
    //         this.chatMessages = apiData || []; // 更新照片列表
    //       }
    //     })
    //     .catch(error => {
    //       // 请求失败，使用本地数据作为备份
    //       console.error('API请求失败:', error);
    //     //   this.loadMedicalData();
    //     });
    // },

    // 从API加载化验报告
    loadMedicalReportFromApi() {
      // 设置请求URL和参数
      const apiUrl = domain + '/medical/show/report';
      const params = { username: this.username };
      
      // 发起GET请求
      axios.get(apiUrl, { params })
        .then(response => {
          // 请求成功，处理返回的数据
          const apiData = response.data;
          console.log('API返回的数据:', apiData);
          // 更新组件数据
          if (apiData) {
            this.analysisReportList = apiData || []; // 更新照片列表
          }
        })
        .catch(error => {
          // 请求失败，使用本地数据作为备份
          console.error('API请求失败:', error);
        });
    },

    // 下载文档
    downloadDocument(doc) {
      if (doc && doc.downloadUrl) {
        try {
          // 添加时间戳参数，避免浏览器缓存并强制下载
          const timestamp = new Date().getTime();
          const downloadUrl = doc.downloadUrl.includes('?') 
            ? `${doc.downloadUrl}&t=${timestamp}&download=true` 
            : `${doc.downloadUrl}?t=${timestamp}&download=true`;
          
          // 直接在当前窗口打开下载链接
          window.location.href = downloadUrl;
          
          // 如果需要在新窗口打开，可以使用以下代码替代
          // window.open(downloadUrl, '_blank');
        } catch (error) {
          console.error('下载文件失败:', error);
          alert('下载文件失败，请稍后重试');
        }
      } else {
        console.error('下载链接不存在');
        alert('下载链接不可用');
      }
    },
    
    // 格式化日期显示
    formatDate(dateStr) {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        return `${parts[1]}-${parts[2]}`;
      }
      return dateStr;
    },
    
    // 切换异常指标详情的显示/隐藏
    toggleReportDetails(index) {
      this.$set(this.expandedReports, index, !this.expandedReports[index]);
    },
    
    // 根据指标名称获取对应的单位
    getUnitByIndicator(name) {
      const unitMap = {
        '血红蛋白': 'g/L',
        '血小板': '10^9/L',
        '红细胞分布宽度': '%',
        '白细胞计数': '10^9/L',
        '中性粒细胞数目': '10^9/L',
        '淋巴细胞数目': '10^9/L',
        '红细胞压积': '%',
        '白蛋白': 'g/L',
        '总蛋白': 'g/L',
        '碱性磷酸酶': 'U/L',
        'y-谷氨酰基转移酶': 'U/L'
      };
      return unitMap[name] || '';
    },
    
    // 获取图表数据点
    getChartPoints(data) {
      // 检查数据是否有效
      if (!data || data.length === 0) {
        return '0,50 300,50'; // 返回一条水平线作为默认值
      }
      
      // 过滤掉无效数据
      const validData = data.filter(item => item && item.value !== undefined && !isNaN(parseFloat(item.value)));
      
      // 如果没有有效数据，返回默认值
      if (validData.length === 0) {
        return '0,50 300,50';
      }
      
      // 将字符串值转换为数字
      const numericData = validData.map(item => ({
        ...item,
        value: parseFloat(item.value)
      }));
      
      // 计算数据的最大值，为了美观可以稍微增加一些空间
      let maxValue = Math.max(...numericData.map(item => item.value)) * 1.2;
      // 确保最大值至少为10，避免数值太小时图表显示不美观
      maxValue = Math.max(maxValue, 10);
      // 向上取整到最接近的5或10的倍数，使刻度更整齐
      maxValue = Math.ceil(maxValue / 5) * 5;
      
      const width = 300; // 图表宽度
      const height = 100; // 图表高度
      
      // 计算每个数据点的坐标
      return numericData.map((item, index) => {
        const x = numericData.length > 1 ? (index / (numericData.length - 1)) * width : width / 2;
        const y = height - (item.value / maxValue) * height;
        return `${x},${y}`;
      }).join(' ');
    },
    
    // 获取Y轴刻度值
    getYAxisValues(indicator) {
      if (!indicator || !indicator.details || indicator.details.length === 0) {
        return [0, 10, 20, 30, 40]; // 默认值
      }
      
      // 过滤掉无效数据
      const validDetails = indicator.details.filter(item => 
        item && item.value !== undefined && !isNaN(parseFloat(item.value))
      );
      
      if (validDetails.length === 0) {
        return [0, 10, 20, 30, 40]; // 默认值
      }
      
      // 将字符串值转换为数字
      const numericDetails = validDetails.map(item => ({
        ...item,
        value: parseFloat(item.value)
      }));
      
      // 计算数据的最大值
      let maxValue = Math.max(...numericDetails.map(item => item.value)) * 1.2;
      // 确保最大值至少为10
      maxValue = Math.max(maxValue, 10);
      // 向上取整到最接近的5或10的倍数
      maxValue = Math.ceil(maxValue / 5) * 5;
      
      // 生成5个均匀分布的刻度值
      const step = maxValue / 4;
      return [maxValue, maxValue - step, maxValue - 2 * step, maxValue - 3 * step, 0].map(val => Math.round(val));
    },
    
    // 获取X轴日期标签
    getXAxisLabels(indicator) {
      if (!indicator || !indicator.details || indicator.details.length === 0) {
        return ['', '', '', '']; // 默认空值
      }
      
      // 过滤有效数据
      const validDetails = indicator.details.filter(item => item && item.date);
      
      if (validDetails.length === 0) {
        return ['', '', '', '']; // 默认空值
      }
      
      // 如果数据点少于4个，生成均匀分布的标签
      if (validDetails.length <= 4) {
        return validDetails.map(item => this.formatDate(item.date));
      }
      
      // 对于更多数据点，选择均匀分布的4个点
      const step = Math.floor(validDetails.length / 3);
      return [
        this.formatDate(validDetails[0].date),
        this.formatDate(validDetails[step].date),
        this.formatDate(validDetails[step * 2].date),
        this.formatDate(validDetails[validDetails.length - 1].date)
      ];
    }
    
  },
  computed: {
    // 根据搜索关键词过滤并按组分类化验报告图片
    groupedLabReports() {
      // 先根据搜索关键词过滤图片
      const filteredImages = this.labReportSearch
        ? this.labReportImages.filter(img => 
            img.desc.toLowerCase().includes(this.labReportSearch.toLowerCase()) ||
            img.date.includes(this.labReportSearch)
          )
        : this.labReportImages;
      
      // 按组分类
      const groups = {};
      filteredImages.forEach(img => {
        if (!groups[img.group]) {
          groups[img.group] = [];
        }
        groups[img.group].push(img);
      });
      
      // 转换为数组格式，便于在模板中使用
      return Object.keys(groups).map(title => ({
        title,
        images: groups[title]
      }));
    }
    }
  }

</script>

<style scoped>
.medical-record {
  max-width: 600px;
  margin: 0 auto;
  background-color: #f5f7fa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

.patient-info {
  background-color: #e9eef8;
  padding: 15px;
  display: flex;
  align-items: center;
}

.avatar {
  width: 40px;
  height: 40px;
  background-color: #333;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-right: 10px;
}

.info .name {
  font-weight: bold;
  font-size: 16px;
}

.info .date {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.tabs {
  display: flex;
  background-color: white;
  border-bottom: 1px solid #eee;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 14px;
  color: #666;
  position: relative;
}

.tab-item.active {
  color: #000;
  font-weight: bold;
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 25%;
  width: 50%;
  height: 2px;
  background-color: #000;
}

.content {
  background-color: white;
  padding: 15px;
}

.section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.photos {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

.photo-item {
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.photo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.photo-item {
  position: relative;
  width: 100%;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.photo-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.15);
}

.photo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.photo-item:hover .photo-overlay {
  opacity: 1;
}

.photo-view-icon {
  color: white;
  font-size: 12px;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
  background-color: #f9f9f9;
  border-radius: 8px;
  margin: 15px 0;
}

.empty-icon {
  font-size: 24px;
  margin-bottom: 10px;
  color: #bbb;
}

.empty-text {
  color: #999;
  font-size: 14px;
}

.history-list {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
}

.history-item {
  margin-bottom: 15px;
  background-color: white;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.photo-category-title {
  font-size: 14px;
  color: #555;
  margin: 10px 0 8px 0;
  padding-left: 8px;
  border-left: 3px solid #4285f4;
  font-weight: 500;
}

.image-error {
  border: 1px dashed #ddd;
  background-color: #f8f8f8;
}

.history-date {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.history-content {
  font-size: 13px;
  line-height: 1.6;
  color: #333;
}

.indicators {
  margin-bottom: 20px;
}

.indicator-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.indicator-name {
  font-size: 14px;
  color: #333;
}

.indicator-value {
  font-size: 14px;
  font-weight: bold;
}

.arrow {
  display: inline-block;
  margin-right: 5px;
  font-weight: bold;
}

.arrow.up {
  color: #e74c3c;
}

.arrow.down {
  color: #27ae60;
}

.charts {
  margin-top: 20px;
}

.chart {
  margin-bottom: 25px;
}

.chart-title {
  font-size: 14px;
  margin-bottom: 10px;
  color: #333;
}

.unit {
  font-size: 12px;
  color: #999;
}

.chart-container {
  display: flex;
  height: 150px;
  position: relative;
  margin-bottom: 5px;
}

.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-right: 10px;
  font-size: 12px;
  color: #999;
}

.chart-line {
  flex: 1;
  position: relative;
  border-left: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.chart-x-axis {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #999;
  padding-left: 25px;
}

.chart-placeholder {
  height: 150px;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.lab-reports {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
}

.lab-report {
  margin-bottom: 15px;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.report-date {
  font-size: 14px;
  font-weight: bold;
  padding: 10px 15px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.toggle-icon {
  font-size: 12px;
  color: #666;
  transition: transform 0.3s ease;
}

.report-details {
  padding: 15px;
}

.report-item {
  margin-bottom: 8px;
  font-size: 13px;
  display: flex;
}

.item-name {
  color: #666;
  flex: 1;
}

.item-value {
  font-weight: bold;
}

.item-value.high {
  color: #e74c3c;
}

.item-value.low {
  color: #27ae60;
}

.item-value.normal {
  color: #333;
}

.empty-state {
  text-align: center;
  padding: 30px 0;
  color: #999;
  font-size: 14px;
}

/* 化验报告搜索框样式 */
.lab-report-search {
  margin-bottom: 15px;
}

.lab-report-input {
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #eee;
  border-radius: 20px;
  font-size: 14px;
  background-color: #f5f5f5;
  color: #333;
  outline: none;
}

/* 化验报告分组样式 */
.lab-report-group {
  margin-bottom: 20px;
}

.lab-report-group-title {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

/* 化验报告图片网格样式 */
.lab-report-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 15px;
}

.lab-report-img-item {
  background-color: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.lab-report-img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  cursor: pointer;
}

.chat-img {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  cursor: pointer;
}

/* 图片预览样式 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.image-preview-container {
  position: relative;
  width: 90vw;
  height: 90vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.1s ease;
  cursor: move;
}

.zoom-controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
}

.zoom-controls button {
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 4px;
  width: 40px;
  height: 40px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-controls button:last-child {
  width: auto;
  padding: 0 10px;
}

.close-preview {
  position: absolute;
  top: -40px;
  right: 0;
  color: white;
  font-size: 30px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 文档列表样式 */
.document-list {
  background-color: #f9f9f9;
}

.document-item {
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: #f0f0f0;
  margin-bottom: 8px;
  position: relative;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.document-item:hover {
  background-color: #e5e5e5;
}

.document-info {
  flex: 1;
}

.document-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 5px;
}

.document-type {
  font-size: 12px;
  color: #666;
}

.document-sender {
  font-size: 12px;
  color: #666;
  position: absolute;
  left: 15px;
  bottom: 15px;
}

.document-date {
  font-size: 12px;
  color: #999;
  position: absolute;
  right: 60px;
  bottom: 15px;
}

.document-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  border-radius: 4px;
}

.excel-icon {
  background-color: #1D6F42;
}

.pdf-icon {
  background-color: #F40F02;
}

.word-icon {
  background-color: #2B579A;
}

.other-icon {
  background-color: #555;
}

/* 聊天记录样式 */
.chat-records {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 10px;
  box-shadow: inset 0 0 10px rgba(0,0,0,0.05);
  max-height: 600px;
  overflow-y: auto;
}

.chat-date {
  margin-bottom: 20px;
}

.chat-date-divider {
  text-align: center;
  font-size: 12px;
  color: #666;
  margin-bottom: 15px;
  position: relative;
  padding: 0 15px;
}

.chat-date-divider::before,
.chat-date-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 30%;
  height: 1px;
  background-color: #e0e0e0;
}

.chat-date-divider::before {
  left: 0;
}

.chat-date-divider::after {
  right: 0;
}

.chat-message {
  display: flex;
  margin-bottom: 16px;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.chat-avatar {
  margin-right: 12px;
  flex-shrink: 0;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  margin-bottom: 15px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.chat-bubble {
  flex: 1;
  max-width: 85%;
  background-color: white;
  padding: 12px 15px;
  border-radius: 18px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  position: relative;
}

.chat-bubble::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 15px;
  width: 15px;
  height: 15px;
  background-color: white;
  transform: rotate(45deg);
  box-shadow: -2px 2px 3px rgba(0,0,0,0.02);
  z-index: -1;
}

.chat-sender {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 6px;
  color: #333;
}

.chat-text {
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  word-break: break-word;
}

.chat-time-stamp {
  font-size: 11px;
  color: #999;
  text-align: right;
  margin-top: 5px;
}

.chat-image {
  margin: 8px 0;
}

.message-img {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.footer {
  padding: 15px;
  display: flex;
  justify-content: center;
  position: sticky;
  bottom: 0;
  background-color: #f5f7fa;
}

.footer-btn {
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 20px;
  padding: 10px 25px;
  font-size: 14px;
  color: #333;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}
</style>